// docs/116 CI7: what the bard is playing now. A tune someone asked for (`inn_song`) plays from the
// moment it began for its whole length; otherwise every SLOT_MS of server time the bard plays the
// slot's own tune (`tunes.ts scheduledTune`), a few seconds after the slot opens, once through, and
// then the room is quiet until the next slot. Everything comes from the server clock and the visit's
// seed, so two players in the same inn hear the same tune from the same moment.
import { useEffect, useState } from 'react';
import { serverNow } from '../../../../core/time';
import { SLOT_MS, TUNES, scheduledTune } from './tunes';
import type { InnData, InnSongView } from './types';
import type { VillageLook } from '../types';

/** The bard waits this long into a slot before starting its tune (a breath between pieces). */
export const SLOT_LEAD_MS = 4_000;

const lengthMs = (tune: number) => Math.round((TUNES[tune]?.seconds ?? 0) * 1000);

/** The window of server ms the slot's own tune plays in. */
export function slotWindow(visitSeed: number, slot: number, season?: string, feast = false): InnSongView & { endMs: number } {
  const tune = scheduledTune(visitSeed, slot, season, feast);
  const startedAtMs = slot * SLOT_MS + SLOT_LEAD_MS;
  return { tune, startedAtMs, by: '', endMs: startedAtMs + lengthMs(tune) };
}

/**
 * The tune playing at `nowMs`: the requested one while it lasts, else the slot's tune while it lasts
 * (unless a request overlapped its window: the bard does not start over one), else undefined.
 */
export function currentSong(requested: InnSongView | undefined, visitSeed: number, nowMs: number, season?: string, feast = false): InnSongView | undefined {
  if (requested && TUNES[requested.tune]) {
    const end = requested.startedAtMs + lengthMs(requested.tune);
    if (nowMs >= requested.startedAtMs && nowMs < end) return requested;
  }
  const slot = Math.floor(nowMs / SLOT_MS);
  const w = slotWindow(visitSeed, slot, season, feast);
  if (nowMs < w.startedAtMs || nowMs >= w.endMs) return undefined;
  if (requested) {
    const rEnd = requested.startedAtMs + lengthMs(requested.tune);
    if (requested.startedAtMs < w.endMs && rEnd > w.startedAtMs) return undefined;
  }
  return { tune: w.tune, startedAtMs: w.startedAtMs, by: '' };
}

/** Seconds into `song` at `nowMs` (0 before it starts). */
export const secondsInto = (song: InnSongView, nowMs: number): number => Math.max(0, (nowMs - song.startedAtMs) / 1000);

/** Server ms until the next slot's tune begins. */
export const nextSlotInMs = (nowMs: number): number => {
  const slot = Math.floor(nowMs / SLOT_MS);
  const start = slot * SLOT_MS + SLOT_LEAD_MS;
  return (nowMs < start ? start : start + SLOT_MS) - nowMs;
};

/** CI15: the first and last week of a season are a feast. */
export const isFeast = (seasonWeek: number): boolean => seasonWeek <= 1 || seasonWeek >= 12;

const same = (a: InnSongView | undefined, b: InnSongView | undefined) => a?.tune === b?.tune && a?.startedAtMs === b?.startedAtMs && a?.by === b?.by;

/** `currentSong` for the room, re-evaluated about once a second on the server clock. */
export function useCurrentSong(data: Pick<InnData, 'song' | 'visitSeed' | 'seasonWeek'>, look: Pick<VillageLook, 'season' | 'seed'>): InnSongView | undefined {
  const seed = data.visitSeed || look.seed;
  const feast = isFeast(data.seasonWeek);
  const calc = () => currentSong(data.song, seed, serverNow(), look.season, feast);
  const [song, setSong] = useState(calc);
  useEffect(() => {
    const tick = () => setSong((was) => { const now = calc(); return same(was, now) ? was : now; });
    tick();
    const t = window.setInterval(tick, 1000);
    return () => window.clearInterval(t);
  }, [data.song?.tune, data.song?.startedAtMs, data.song?.by, seed, look.season, feast]);
  return song;
}
